/**
 * Typed API Client
 *
 * A thin, type-safe wrapper around the existing `apiClient.js` fetch helpers.
 * Uses generated OpenAPI types so callers get compile-time safety on request
 * bodies and response shapes without changing any runtime behaviour.
 *
 * Usage:
 *   import { typedGet, typedPost } from '@/api/client';
 *   const notes = await typedGet<GetNotesResponse>('/notes');
 *   const created = await typedPost<CreateNoteResponse, CreateNoteRequest>('/notes', body);
 *
 * To regenerate the backing types:
 *   pnpm generate:api-types   (requires FastAPI running on :8001)
 */

import {
  get as rawGet,
  post as rawPost,
  put as rawPut,
  patch as rawPatch,
  deleteRequest as rawDelete,
  apiFetch,
  buildApiUrl,
} from '../utils/apiClient';

// Domain types live in dedicated files under src/types/generated/.
// Import from there (e.g. notes-types.ts, calendar-types.ts) rather than
// from api-types.ts directly — the generated file has @ts-nocheck due to
// known openapi-typescript index-signature conflicts.

/* -------------------------------------------------------------------------- */
/*  Low-level typed wrappers                                                  */
/* -------------------------------------------------------------------------- */

/** Options forwarded to the underlying fetchWithAuth call. */
export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** Per-request timeout in ms (default 30 000). */
  timeout?: number;
}

/**
 * Typed GET — returns parsed JSON narrowed to `TResponse`.
 *
 * The underlying `get()` from apiClient returns a raw `Response`.
 * This helper calls `.json()` and casts the result.
 */
export async function typedGet<TResponse>(
  endpoint: string,
  options?: RequestOptions,
): Promise<TResponse> {
  const res = await rawGet(endpoint, options);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text, endpoint);
  }
  return (await res.json()) as TResponse;
}

/**
 * Typed POST — sends `body` as JSON and returns parsed `TResponse`.
 *
 * The underlying `rawPost` already throws on `!res.ok`, but it throws a
 * plain `Error` with ad-hoc `.status` / `.data` properties.  We catch
 * that and re-throw as `ApiError` so every typed method surfaces the
 * same error shape.
 */
export async function typedPost<TResponse, TBody = unknown>(
  endpoint: string,
  body: TBody,
  options?: RequestOptions,
): Promise<TResponse> {
  try {
    const res = await rawPost(endpoint, body, options);
    return (await res.json()) as TResponse;
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    // rawPost throws plain Error with .status / .data — normalise it.
    const status = (err as Record<string, unknown>)?.status;
    const data = (err as Record<string, unknown>)?.data;
    if (typeof status === 'number') {
      throw new ApiError(status, JSON.stringify(data ?? ''), endpoint);
    }
    throw err;
  }
}

/**
 * Typed PUT — sends `body` as JSON and returns parsed `TResponse`.
 */
export async function typedPut<TResponse, TBody = unknown>(
  endpoint: string,
  body: TBody,
  options?: RequestOptions,
): Promise<TResponse> {
  const res = await rawPut(endpoint, body, options);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text, endpoint);
  }
  return (await res.json()) as TResponse;
}

/**
 * Typed PATCH — sends `body` as JSON and returns parsed `TResponse`.
 *
 * Same normalisation as typedPost — rawPatch throws a plain `Error` on
 * `!res.ok`, which we convert to `ApiError`.
 */
export async function typedPatch<TResponse, TBody = unknown>(
  endpoint: string,
  body: TBody,
  options?: RequestOptions,
): Promise<TResponse> {
  try {
    const res = await rawPatch(endpoint, body, options);
    return (await res.json()) as TResponse;
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    const status = (err as Record<string, unknown>)?.status;
    const data = (err as Record<string, unknown>)?.data;
    if (typeof status === 'number') {
      throw new ApiError(status, JSON.stringify(data ?? ''), endpoint);
    }
    throw err;
  }
}

/**
 * Typed DELETE — returns parsed `TResponse` (often `void` / empty).
 */
export async function typedDelete<TResponse = void>(
  endpoint: string,
  options?: RequestOptions,
): Promise<TResponse> {
  const res = await rawDelete(endpoint, options);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text, endpoint);
  }
  // Some DELETE endpoints return 204 with no body.
  const text = await res.text();
  if (!text) return undefined as unknown as TResponse;
  return JSON.parse(text) as TResponse;
}

/**
 * Typed binary fetch — returns the raw `Response` for blob / stream use.
 * Useful for PDF exports, file downloads, etc.
 */
export async function typedFetchRaw(
  endpoint: string,
  init?: RequestInit,
): Promise<Response> {
  return apiFetch(buildApiUrl(endpoint), { credentials: 'include', ...init });
}

/* -------------------------------------------------------------------------- */
/*  Error type                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Structured API error with status code and optional response body.
 *
 * The message unwraps FastAPI's `detail` payloads (string or
 * `{code, message, ...}`) the same way `apiErrorMessage` in
 * `utils/apiClient` does, so toasts show the human-readable message
 * instead of raw JSON.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    public readonly endpoint: string,
  ) {
    super(_toMessage(status, body, endpoint));
    this.name = 'ApiError';
  }
}

function _toMessage(status: number, body: string, endpoint: string): string {
  try {
    const parsed = JSON.parse(body);
    const detail = parsed?.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : detail?.message || parsed?.error?.message || parsed?.message;
    if (message) return message;
  } catch {
    // body is not JSON — fall through to the raw-context message
  }
  return `API ${status} on ${endpoint}: ${body.slice(0, 200)}`;
}
